import { useState } from "react"
import { X } from "lucide-react"
import { UnauthenticatedWelcome } from "./UnauthenticatedWelcome"
import { HomeActionPromoBar } from "./HomeActionPromoBar"
import { Input, StatusMessage } from "../ui"
import { useLocalization } from "../../lib/i18n"

type HomeWelcomeProps = {
  isAuthenticated: boolean;
  hasPools: boolean;
  onLogin: () => void;
  onSignUp: () => void;
  joinPoolCode?: string;
  isJoiningPool?: boolean;
  joinPoolFeedback?: string | null;
  joinPoolFeedbackKind?: "success" | "error" | "info" | null;
  onJoinPoolCodeChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onJoinPoolSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  onCreatePool?: () => void;
  soccerPromoCollapsed?: boolean;
  onSoccerPromoCollapsedChange?: (collapsed: boolean) => void;
};

export function HomeWelcome({
  isAuthenticated,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  hasPools,
  onLogin,
  onSignUp,
  joinPoolCode = "",
  isJoiningPool = false,
  joinPoolFeedback = null,
  joinPoolFeedbackKind = null,
  onJoinPoolCodeChange,
  onJoinPoolSubmit,
  onCreatePool,
  soccerPromoCollapsed = false,
  onSoccerPromoCollapsedChange,
}: HomeWelcomeProps) {
  const { t } = useLocalization()
  const [isJoinSheetOpen, setIsJoinSheetOpen] = useState(false)

  if (isAuthenticated) {
    return (
      <div className="min-w-0 space-y-4">
        <HomeActionPromoBar
          onJoinPool={() => setIsJoinSheetOpen(true)}
          onCreatePool={onCreatePool}
          canJoinPool={Boolean(onJoinPoolSubmit)}
          collapsed={soccerPromoCollapsed}
          onCollapsedChange={onSoccerPromoCollapsedChange}
        />

        {isJoinSheetOpen ? (
          <div
            className="fixed inset-0 z-40 flex items-end bg-black/70 md:items-center md:justify-center md:p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="join-pool-title"
            onClick={() => setIsJoinSheetOpen(false)}
          >
            <form
              className="w-full rounded-t-2xl border border-border bg-background-surface px-5 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-3 shadow-layer md:max-w-md md:rounded-2xl md:p-6"
              onSubmit={onJoinPoolSubmit}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="mx-auto mb-6 h-1 w-10 rounded-full bg-border" aria-hidden="true" />
              <div className="mb-5 flex items-start justify-between gap-4">
                <div>
                  <h2 id="join-pool-title" className="font-display text-4xl font-black uppercase text-foreground">
                    {t("home.join.title")}
                  </h2>
                  <p className="mt-2 text-lg font-bold text-brand">{t("home.join.subtitle")}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsJoinSheetOpen(false)}
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border text-foreground-muted transition hover:border-brand/60 hover:text-foreground"
                  aria-label="Close join pool"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>

              <div className="mb-5 space-y-2 rounded-lg border border-brand/40 bg-brand/5 px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-foreground-faint">{t("home.join.picksUnlock")}</span>
                  <span className="text-sm font-black text-success">{t("home.join.immediately")}</span>
                </div>
                <p className="text-sm font-semibold text-foreground-faint">{t("home.join.accessPolicy")}</p>
              </div>

              <label
                htmlFor="home-join-pool-code"
                className="mb-2 block text-xs font-semibold uppercase tracking-wide text-foreground-faint"
              >
                {t("home.join.fieldLabel")}
              </label>
              <Input
                id="home-join-pool-code"
                name="joinPoolCode"
                value={joinPoolCode}
                onChange={onJoinPoolCodeChange}
                placeholder={t("home.join.placeholder")}
                disabled={isJoiningPool}
                maxLength={9}
                autoComplete="off"
                className="h-14 uppercase !rounded-lg !border-border !bg-[#1f212f]/80 px-5 !font-mono !text-2xl !font-semibold tracking-[0.18em]"
              />

              {joinPoolFeedback && (
                <StatusMessage
                  id="join-pool-feedback"
                  variant={
                    joinPoolFeedbackKind === "success"
                      ? "success"
                      : joinPoolFeedbackKind === "error"
                        ? "error"
                        : "info"
                  }
                  className="mt-3 !py-1.5 !text-xs"
                >
                  {joinPoolFeedback}
                </StatusMessage>
              )}

              <button
                type="submit"
                disabled={isJoiningPool || !joinPoolCode.trim() || !onJoinPoolSubmit}
                className="mt-5 min-h-[54px] w-full rounded-lg border border-brand bg-brand px-4 text-base font-bold text-black transition hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isJoiningPool ? "..." : t("home.join.submit")}
              </button>
            </form>
          </div>
        ) : null}

      </div>
    )
  }

  return <UnauthenticatedWelcome onSignUp={onSignUp} onSignIn={onLogin} />
}
